import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useProducts } from '@/features/product-scan/useProducts';

import { SettingsCard, SettingsSection } from './settings-ui';
import { useProductPriority, useUpdateProductPriority } from './useSettings';

/**
 * Settings › Product priority (SCRUM-46): a Bank Admin sets a global rank order
 * for products. Ranked products render in this order across the drawer Recos
 * and the Refer popover; when no order is configured, those surfaces fall back
 * to match-score order — the feature is strictly additive.
 *
 * Ordering is display-level only: it never affects eligibility evaluation or
 * match counts. Reordering uses up/down controls (no drag-and-drop dependency;
 * drag is a possible later enhancement).
 */
export function ProductPrioritySection() {
  const { data: products, isLoading: productsLoading } = useProducts();
  const { order: savedOrder, isLoading: orderLoading } = useProductPriority();
  const { save } = useUpdateProductPriority();

  // Local working order of product ids. Initialized from the saved order, with
  // any products not yet in the saved order appended (by catalog order) so a
  // newly-added product never disappears (plan G2).
  const catalogIds = useMemo(() => (products ?? []).map((p) => p.id), [products]);
  const [workingOrder, setWorkingOrder] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  useEffect(() => {
    if (productsLoading || orderLoading) return;
    const saved = savedOrder ?? [];
    const inCatalog = saved.filter((id) => catalogIds.includes(id));
    const missing = catalogIds.filter((id) => !inCatalog.includes(id));
    setWorkingOrder([...inCatalog, ...missing]);
  }, [savedOrder, catalogIds, productsLoading, orderLoading]);

  const nameById = useMemo(() => new Map((products ?? []).map((p) => [p.id, p.name])), [products]);

  const move = (index: number, delta: number) => {
    setWorkingOrder((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const a = prev[index];
      const b = prev[target];
      if (a === undefined || b === undefined) return prev;
      const next = [...prev];
      next[index] = b;
      next[target] = a;
      return next;
    });
    setSavedTick(false);
  };

  const onSave = () => {
    setSaving(true);
    save(workingOrder)
      .then(() => {
        setSavedTick(true);
      })
      .catch(() => {
        setSavedTick(false);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const isLoading = productsLoading || orderLoading;

  return (
    <SettingsSection>
      <SettingsCard
        title="Product priority"
        headerRight={
          <Button onClick={onSave} disabled={saving || isLoading || workingOrder.length === 0}>
            {saving ? 'Saving…' : savedTick ? 'Saved' : 'Save order'}
          </Button>
        }
      >
        <p className="text-text-2 mb-3 text-[0.8125rem]">
          Set the order products appear in recommendations. Highest priority first. This affects
          display order only — it does not change eligibility or match counts.
        </p>

        {isLoading ? (
          <div className="text-text-3 py-6 text-center text-sm">Loading products…</div>
        ) : workingOrder.length === 0 ? (
          <div className="text-text-3 py-6 text-center text-sm">
            No products configured for this tenant yet.
          </div>
        ) : (
          <ol className="border-border-1 divide-border-1 divide-y overflow-hidden rounded-md border">
            {workingOrder.map((id, i) => (
              <li
                key={id}
                className="flex items-center justify-between px-3 py-2"
                data-testid="priority-row"
              >
                <span className="flex items-center gap-2 text-sm">
                  <span className="text-text-3 w-5 font-mono text-xs">{i + 1}</span>
                  <span className="text-text-0">{nameById.get(id) ?? id}</span>
                </span>
                <span className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-label={`Move ${nameById.get(id) ?? id} up`}
                    disabled={i === 0}
                    onClick={() => {
                      move(i, -1);
                    }}
                    className="text-text-3 hover:text-text-0 rounded p-1 text-xs disabled:opacity-30"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${nameById.get(id) ?? id} down`}
                    disabled={i === workingOrder.length - 1}
                    onClick={() => {
                      move(i, 1);
                    }}
                    className="text-text-3 hover:text-text-0 rounded p-1 text-xs disabled:opacity-30"
                  >
                    ▼
                  </button>
                </span>
              </li>
            ))}
          </ol>
        )}
      </SettingsCard>
    </SettingsSection>
  );
}
